/**
 *  Example usage:
 *  <video-player class="muted group block">
      {{ section.settings.video | video_tag: preload: "none", muted: true, autoplay: false, loop: true, playsinline: true, controls: false, image_size: '1440x', class: video_class }}
      <div class="mt-4 flex items-center gap-x-4">
        <button type="button" data-video="play-pause">
          {%- render 'global-icon', icon: 'play', icon_class: 'w-6 group-[.playing]:hidden' -%}
          {%- render 'global-icon', icon: 'pause', icon_class: 'w-6 hidden group-[.playing]:block' -%}
        </button>
        <button type="button" data-video="mute">
          {%- render 'global-icon', icon: 'sound', icon_class: 'w-6 group-[.muted]:hidden' -%}
          {%- render 'global-icon', icon: 'muted', icon_class: 'w-6 hidden group-[.muted]:block' -%}
        </button>
      </div>
    </video-player>
 */

if (!customElements.get('video-player')) {
  /**
   * class VideoPlayer
   * @extends HTMLElement
   * @property {HTMLVideoElement | null} video
   */
  class VideoPlayer extends HTMLElement {
    /** @type {HTMLVideoElement | null} */

    constructor() {
      super();

      this.video = this.querySelector('video');
    }

    connectedCallback() {
      if (!this.video) return;

      // 👇 This line is the key fix for Safari autoplay issues with <source>
      this.video.load();

      // Select elements in the dom that we need for the video player
      this.playPause = this.querySelector('[data-video="play-pause"]');
      this.mute = this.querySelector('[data-video="mute"]');

      // Add event listeners to elements
      this.playPause?.addEventListener('click', () => this.togglePlayPause());
      this.mute?.addEventListener('click', () => this.toggleMute());
    }

    /**
     * togglePlayPause
     * @description Set the video to play or pause.
     */
    togglePlayPause() {
      this.video.paused ? this.playVideo() : this.pauseVideo();
    }

    /**
     * toggleMute
     * @description Set the video to mute or unmute.
     */
    toggleMute() {
      this.video.muted = !this.video.muted;
      this.video.muted ? this.unmuteVideo() : this.muteVideo();
    }

    /**
     * unmuteVideo
     */
    unmuteVideo() {
      this.video.removeAttribute('muted');
      this.classList.add('muted');
    }

    /**
     * muteVideo
     */
    muteVideo() {
      this.video.setAttribute('muted', true);
      this.classList.remove('muted');
    }

    /**
     * pauseVideo
     */
    pauseVideo() {
      this.video.pause();
      this.classList.remove('playing');
    }

    /**
     * playVideo
     */
    playVideo() {
      this.video.play();
      this.classList.add('playing');
    }
  }

  window.VideoPlayer = VideoPlayer;
  customElements.define('video-player', VideoPlayer);
}
